import React, { useState, useEffect } from "react";
import { ArrowLeft, RefreshCw, Trophy, HelpCircle } from "lucide-react";
import { Link } from "react-router-dom";

const FourFoursGame = () => {
  const [targetNumber, setTargetNumber] = useState(0);
  const [expression, setExpression] = useState("");
  const [history, setHistory] = useState(() => {
    const saved = localStorage.getItem("fourFoursHistory");
    return saved ? JSON.parse(saved) : {};
  });
  const [message, setMessage] = useState({ type: "", text: "" });
  const [showRules, setShowRules] = useState(false);

  useEffect(() => {
    localStorage.setItem("fourFoursHistory", JSON.stringify(history));
  }, [history]);

  // Find the next unsolved number
  useEffect(() => {
    let nextTarget = 0;
    while (history[nextTarget]) {
      nextTarget++;
    }
    // Cap at 50, but allow playing if everything is solved? 
    // The requirement says "win if they make it to 50".
    if (nextTarget > 50) {
        setTargetNumber(50); // Or maybe a "You Won" state
    } else {
        setTargetNumber(nextTarget);
    }
  }, [history]);

  const insertChar = (char) => {
    setExpression((prev) => prev + char);
  };

  const backspace = () => {
    setExpression((prev) => prev.slice(0, -1));
  };

  const clear = () => {
    setExpression("");
    setMessage({ type: "", text: "" });
  };

  // Safe Math Evaluation
  const evaluateExpression = (expr) => {
    try {
      // 1. Replace symbols for JS evaluation
      let evalExpr = expr
        .replace(/×/g, "*")
        .replace(/÷/g, "/") // classic division symbol if I use it
        .replace(/−/g, "-"); // classic minus

      // 2. Handle factorial (!) - simplistic approach: replace "number!" with "factorial(number)"
      // This regex looks for a number or a parenthesized group followed by !
      // Valid: 4!, (4+4)!
      // We need to execute this iteratively to handle nested/multiple factorials
      while (evalExpr.includes("!")) {
        evalExpr = evalExpr.replace(/(\d+|\([^)]+\))!/g, (match, number) => {
           return `factorial(${number})`;
        });
        // If no change, we might have invalid syntax or just stuck, break to avoid infinite loop
        if (evalExpr.includes("!")) break; 
      }

      // 3. Handle Square Root (√)
      // Must handle √4 -> sqrt(4), √.4 -> sqrt(0.4), √(4+4) -> sqrt(4+4)
      // We'll run this in a loop to handle nested roots like √√16
      while (evalExpr.includes("√")) {
         // Pattern: √ followed by:
         // 1. A parenthesized group: \([^)]+\)
         // 2. A number (potentially decimal): \d+(\.\d+)?|\.\d+
         const regex = /√(\([^)]+\)|\d+(\.\d+)?|\.\d+)/;
         if (!regex.test(evalExpr)) break; // Safety break if format doesn't match expected
         evalExpr = evalExpr.replace(regex, "sqrt($1)");
      }

      // Define scope for eval
      const factorial = (n) => {
        if (n < 0) throw new Error("Negative factorial");
        if (n > 100) throw new Error("Factorial too large"); // Safety
        if (!Number.isInteger(n)) throw new Error("Non-integer factorial");
        let result = 1;
        for (let i = 2; i <= n; i++) result *= i;
        return result;
      };
      
      const sqrt = (n) => Math.sqrt(n);

      // We need to be careful with 'eval'. 
      // A safer alternative is Function constructor with limited scope, but still risky if user types arbitrary code.
      // Since we limit buttons, user can only type what we give them? 
      // If we use a text input, we must validate characters.
      
      // Let's rely on validation: only allow digits, operators, parens, ., !, √
      if (/[^0-9+\-*/().!\s√]/.test(expr)) {
          throw new Error("Invalid characters");
      }

      // eslint-disable-next-line
      const result = new Function("factorial", "sqrt", `return ${evalExpr}`)(factorial, sqrt);
      
      return result;
    } catch (err) {
      throw new Error("Invalid expression");
    }
  };

  const checkSolution = () => {
    try {
      // 1. Check for exactly four 4s
      const fours = (expression.match(/4/g) || []).length;
      if (fours !== 4) {
        setMessage({ type: "error", text: `You used ${fours} fours. You must use exactly four!` });
        return;
      }
      
      // 2. Check for other numbers (only 4 is allowed)
      // The regex [0-35-9] matches any digit except 4.
      if (/[0-35-9]/.test(expression)) {
          setMessage({ type: "error", text: "Only the digit 4 is allowed!" });
          return;
      }

      // 3. Evaluate
      const result = evaluateExpression(expression);

      // 4. Check precision handling (floating point issues)
      // e.g. 4/4 = 1.0
      if (Math.abs(result - targetNumber) < 1e-9) {
        // Success!
        const newHistory = { ...history, [targetNumber]: expression };
        setHistory(newHistory);
        setMessage({ type: "success", text: `Correct! ${expression} = ${targetNumber}` });
        setExpression("");
        
        // Celebrate if 50 is reached
        if (targetNumber === 50) {
            // Game Won Logic trigger
        }
      } else {
        setMessage({ type: "error", text: `Result is ${Number(result.toFixed(4))}, not ${targetNumber}` });
      }

    } catch (err) {
      setMessage({ type: "error", text: "Invalid expression syntax" });
    }
  };
  
  const resetGame = () => {
      if (window.confirm("Are you sure you want to clear all progress?")) {
          setHistory({});
          setTargetNumber(0);
          setExpression("");
          setMessage({ type: "", text: "" });
      }
  };

  const solvedCount = Object.keys(history).length;
  const isComplete = solvedCount > 50;

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <Link
            to="/games"
            className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            Back to Games
          </Link>
          <button
            onClick={() => setShowRules(!showRules)}
            className="text-gray-600 hover:text-gray-900"
          >
            <HelpCircle className="h-6 w-6" />
          </button>
        </div>

        {/* Game Container */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-blue-600 p-6 text-center text-white relative">
             <h1 className="text-3xl font-bold mb-2">Four Fours</h1>
             <p className="opacity-90">Target Number</p>
             <div className="text-6xl font-black mt-2">{isComplete ? "🏆" : targetNumber}</div>
             {isComplete && <p className="mt-2 font-bold">ALL LEVELS CLEARED!</p>}
             
             <div className="absolute top-4 right-4 text-sm bg-blue-700 px-3 py-1 rounded-full">
                 {solvedCount} / 51
             </div>
          </div>

          <div className="p-6">
            {showRules && (
                <div className="mb-6 bg-blue-50 p-4 rounded-lg text-sm text-blue-800">
                    <h3 className="font-bold mb-2">How to Play</h3>
                    <ul className="list-disc pl-4 space-y-1">
                        <li>Use exactly four 4s to make the target number.</li>
                        <li>Allowed operators: +, −, ×, /, (), ., √, !</li>
                        <li>You must solve numbers in order from 0 to 50.</li>
                        <li>Example for 0: 44 − 44</li>
                    </ul>
                </div>
            )}

            {/* Display Area */}
            <div className="bg-gray-100 p-4 rounded-xl mb-6 text-right relative min-h-[4rem] flex items-center justify-end">
                <span className="text-3xl font-mono text-gray-800 tracking-wider">
                    {expression || <span className="text-gray-400">Enter expression</span>}
                </span>
            </div>

            {/* Message Area */}
            {message.text && (
                <div className={`mb-6 p-3 rounded-lg text-center text-sm font-medium ${
                    message.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                }`}>
                    {message.text}
                </div>
            )}

            {/* Keypad */}
            {!isComplete && (
            <div className="grid grid-cols-4 gap-3">
              <button onClick={() => insertChar('(')} className="p-3 bg-gray-200 rounded-lg hover:bg-gray-300 font-bold text-lg text-gray-700">(</button>
              <button onClick={() => insertChar(')')} className="p-3 bg-gray-200 rounded-lg hover:bg-gray-300 font-bold text-lg text-gray-700">)</button>
              <button onClick={() => insertChar('√')} className="p-3 bg-gray-200 rounded-lg hover:bg-gray-300 font-bold text-lg text-gray-700">√</button>
              <button onClick={clear} className="p-3 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 font-bold text-sm">AC</button>
              
              <button onClick={() => insertChar('!')} className="p-3 bg-gray-200 rounded-lg hover:bg-gray-300 font-bold text-lg text-gray-700">!</button>
              <button onClick={() => insertChar('/')} className="p-3 bg-gray-200 rounded-lg hover:bg-gray-300 font-bold text-lg text-gray-700">/</button>
              <button onClick={() => insertChar('*')} className="p-3 bg-gray-200 rounded-lg hover:bg-gray-300 font-bold text-lg text-gray-700">×</button>
              <button onClick={backspace} className="p-3 bg-orange-100 text-orange-600 rounded-lg hover:bg-orange-200 font-bold text-sm">DEL</button>
              
              <button onClick={() => insertChar('4')} className="p-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-bold text-xl col-span-1 shadow-md active:transform active:scale-95 transition-all">4</button>
              <button onClick={() => insertChar('+')} className="p-3 bg-gray-200 rounded-lg hover:bg-gray-300 font-bold text-lg text-gray-700">+</button>
              <button onClick={() => insertChar('-')} className="p-3 bg-gray-200 rounded-lg hover:bg-gray-300 font-bold text-lg text-gray-700">−</button>
              <button onClick={() => insertChar('.')} className="p-3 bg-gray-200 rounded-lg hover:bg-gray-300 font-bold text-lg text-gray-700">.</button>

              <button onClick={checkSolution} className="col-span-4 p-4 bg-green-500 text-white rounded-xl hover:bg-green-600 font-bold text-lg shadow-md mt-2 active:transform active:scale-95 transition-all">
                  CHECK
              </button>
            </div>
            )}
            
            {isComplete && (
                <div className="text-center py-8">
                     <Trophy className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
                     <h3 className="text-2xl font-bold text-gray-800 mb-2">Congratuations!</h3>
                     <p className="text-gray-600 mb-6">You have mastered the Four Fours challenge.</p>
                     <button onClick={resetGame} className="flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 mx-auto">
                        <RefreshCw className="mr-2 h-5 w-5" /> Play Again
                     </button>
                </div>
            )}
          </div>
          
          {/* History Footer */}
          <div className="bg-gray-50 p-4 border-t border-gray-100">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Recent Solutions</h4>
              <div className="flex flex-wrap gap-2">
                  {Object.entries(history).sort((a,b) => b[0] - a[0]).slice(0, 5).map(([num, expr]) => (
                      <span key={num} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {num} = {expr}
                      </span>
                  ))}
                  {solvedCount === 0 && <span className="text-xs text-gray-400 italic">No solutions yet</span>}
              </div>
          </div>
        </div>
        
        <div className="mt-8 text-center">
            <button onClick={resetGame} className="text-sm text-gray-500 hover:text-red-500 underline">
                Reset Progress
            </button>
        </div>
      </div>
    </div>
  );
};

export default FourFoursGame;
